import { ColorInput as MantineColorInput } from "@mantine/core";
import * as React from "react";

import type { ElementProps, SizeValue, StyleProps } from "../primitives/style-props";

/**
 * The colour composite: `ColorInput` (SC-3625).
 *
 * **Why this is a composite and not a control.** It renders a `Popover` — the saturation area, hue
 * slider and swatches live in a dropdown — and `controls/README.md` defines that tier as
 * value-contract-plus-controlled-state with no portal. Every other dropdown-bearing input in this
 * repo (`Select`, `MultiSelect`, `Autocomplete`, the four date controls) sits here for the same
 * reason; `NativeSelect` is in `controls` precisely because a native `<select>` opens no overlay.
 *
 * THE VALUE CONTRACT, and the one thing a caller must not get wrong. `value` and `onChange` are a
 * plain hex string, and `onChange` fires on EVERY keystroke with whatever is in the field —
 * including `#`, `#2`, `#2b` on the way to `#2b4a8b`. A controlled caller therefore has to accept
 * those intermediate values back into `value`, or the field cannot be typed into at all: reject
 * them and the input freezes at its old colour, one character at a time.
 *
 * A caller that must not PERSIST an incomplete colour keeps the transient text in its own state and
 * commits only when it is complete. `OutputTemplateColorField` in the output-templates module is
 * the worked example. Do not rely on Mantine's `fixOnBlur` for this: it is left at its default and
 * may tidy the field in a real browser, but it does not restore the last valid colour under jsdom,
 * so it is not a guarantee anything should be built on.
 *
 * One vendor behaviour is deliberately kept: the eye dropper, where the browser supports it. It is
 * a read of the screen the user initiates, it reaches no application data, and on a label editor it
 * is the fastest way to match an existing brand colour.
 *
 * Mantine 7 portals a Popover by default while Mantine 6 rendered it inline. This wrapper follows
 * the same decision the date composites made (SC-2703) and keeps Mantine 6's inline default, so an
 * ancestor with `overflow: hidden` clips it the same way it clips every other owned dropdown until
 * a call site asks to portal. That keeps one rule across the seam rather than a per-component
 * surprise.
 *
 * `format` is NOT on the owned surface. Mantine can emit rgba/hsl/hex-alpha, and the only consumer
 * — the output-template editor — stores six-digit hex because its renderers slice the string by
 * byte offset. Offering the other formats through this seam would let a caller store a colour the
 * PDF renderer draws as black. Add it back with a call site that needs it, and check what that call
 * site persists first.
 */

/** The label and feedback surface, matching what `form.getInputProps()` feeds. */
interface ColorFieldProps {
  label?: React.ReactNode;
  description?: React.ReactNode;
  error?: React.ReactNode;
  withAsterisk?: boolean;
  required?: boolean;
  placeholder?: string;
  /**
   * Narrowed to the two keys a call site has any reason to set, exactly as the date composites
   * narrow theirs. The rest of Mantine's Popover surface is deliberately not restated.
   */
  popoverProps?: { withinPortal?: boolean; zIndex?: number };
  /** Non-numeric: Mantine resolves input size by theme key only, so a number would produce no size. */
  size?: Exclude<SizeValue, number>;
}

export interface ColorInputProps
  extends Omit<ElementProps<"input">, "size" | "onChange" | "value" | "defaultValue">, StyleProps, ColorFieldProps {
  /** Hex colour, controlled. */
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  /** Preset colours under the picker. Hex strings; an empty list hides the row. */
  swatches?: string[];
  /** Swatches per row. Mantine's own default is 7 (or 10 when `swatches` is set). */
  swatchesPerRow?: number;
  /** Closes the dropdown when a swatch is clicked. Off by default, as in Mantine. */
  closeOnColorSwatchClick?: boolean;
  /** Hides the saturation area and hue slider, leaving hex entry and swatches. */
  disallowInput?: boolean;
  /** Left-section preview swatch. On by default. */
  withPreview?: boolean;
  /** Eye-dropper button in the right section. On by default where the browser supports it. */
  withEyeDropper?: boolean;
  /**
   * Owned name at the Mantine 6 spelling, forwarded as `leftSection` (SC-2703). Setting it replaces
   * the preview swatch, so pass `withPreview={false}` alongside it.
   */
  icon?: React.ReactNode;
  variant?: "default" | "filled" | "unstyled";
}

/** Mantine 6 rendered a Popover inline; Mantine 7 portals it. Keep the Mantine 6 default. */
const withMantine6PortalDefault = (popoverProps: ColorFieldProps["popoverProps"]) => ({
  ...popoverProps,
  withinPortal: popoverProps?.withinPortal ?? false,
});

export const ColorInput = React.forwardRef<HTMLInputElement, ColorInputProps>(function ColorInput({ icon, popoverProps, ...props }, ref) {
  return (
    <MantineColorInput
      {...props}
      // Hex only, for the reason recorded in the module comment above.
      format="hex"
      leftSection={icon}
      popoverProps={withMantine6PortalDefault(popoverProps)}
      data-sc-input="true"
      ref={ref}
    />
  );
});
